import { useState, useEffect } from 'react';

const API = 'https://alterus.onrender.com';

function Spinner() {
  return <span style={{ display: 'inline-block', width: 12, height: 12, border: '2px solid #1e2235', borderTop: '2px solid #6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite', verticalAlign: 'middle', marginRight: 6 }} />;
}

const STYLE_COLORS = {
  direct: '#EF4444', collaborative: '#22C55E', formal: '#6366F1',
  casual: '#F59E0B', analytical: '#06B6D4', unknown: '#5A607A',
};
const HEALTH_COLORS = {
  strong: '#22C55E', neutral: '#5A607A', needs_attention: '#EF4444',
};

// Seeded stakeholders reflecting your real network
const SEED_STAKEHOLDERS = [
  { name: 'Jason Wong',   role: 'VP Engineering',    workstream: 'Customer Engine',      relationship_health: 'strong',          communication_style: 'direct',        key_topics: ['Q2 priorities', 'Customer Engine', 'roadmap'],        draft_instruction: 'Lead with status and numbers. Jason values directness and wants the bottom line first.',   preferred_tone: 'Concise, data-driven, VP-friendly',     response_triggers: ['Clear status', 'Numbers & timelines', 'Risks surfaced early'], avoid: ['Vague updates', 'Over-explaining'],            relationship_signal: 'Active collaboration on Q2 priorities',      source: 'seeded' },
  { name: 'Raghu',        role: 'Director, EDP',     workstream: 'ZTSD ML',              relationship_health: 'neutral',         communication_style: 'analytical',    key_topics: ['ML model deployment', 'ZTSD', 'EDP planning'],        draft_instruction: 'Raghu is analytical — lead with timelines and specifics. Avoid ambiguity on deliverables.', preferred_tone: 'Structured and technical',               response_triggers: ['Specific timelines', 'Dependency clarity'],    avoid: ['Vague commitments', 'Missing blockers'],       relationship_signal: 'Needs ZTSD timeline soon',                    source: 'seeded' },
  { name: 'Sibanjan Das', role: 'Tech Lead, EAI',    workstream: 'EAI / Semantic Router', relationship_health: 'strong',         communication_style: 'collaborative', key_topics: ['Semantic router', 'Prod deployments', 'Sprint scope'], draft_instruction: 'Sibanjan is a close technical collaborator. Keep it concise and action-oriented.',         preferred_tone: 'Peer-level, technical, direct',         response_triggers: ['Clear ask', 'Technical specifics'],            avoid: ['Overly formal language'],                      relationship_signal: 'Strong day-to-day collaboration',             source: 'seeded' },
  { name: 'Jerry Jiang',  role: 'Engineer, EAI',     workstream: 'NBA Agent',            relationship_health: 'neutral',         communication_style: 'direct',        key_topics: ['NBA agent', 'Dual-model vs single router', 'Sprint'],  draft_instruction: 'Jerry needs crisp decisions. State the decision or ask clearly, no preamble.',             preferred_tone: 'Fast, technical, no fluff',             response_triggers: ['Firm decision', 'Quick reply'],                avoid: ['Long explanations', 'Deferring decisions'],    relationship_signal: 'Awaiting architecture decision',              source: 'seeded' },
  { name: 'Senthil V.',   role: 'Engineer, EAI',     workstream: 'Sprint / Stardog',     relationship_health: 'needs_attention', communication_style: 'direct',        key_topics: ['Sprint items', 'Stardog vs Neo4j', 'Overdue tasks'],  draft_instruction: 'Senthil is flagging overdue items — acknowledge and give a clear resolution timeline.',   preferred_tone: 'Direct, responsive, accountable',       response_triggers: ['Acknowledgment', 'Timeline commitment'],       avoid: ['Silence', 'Vague "looking into it"'],          relationship_signal: '3 sprint items past due — needs sync',        source: 'seeded' },
  { name: 'Amin',         role: 'Deputy CISO',       workstream: 'Halliburton PLEP',     relationship_health: 'neutral',         communication_style: 'formal',        key_topics: ['AI strategy', 'PLEP board session', 'Security'],      draft_instruction: 'Amin is executive-level and security-focused. Be formal, precise, and board-ready.',      preferred_tone: 'Executive, formal, security-aware',     response_triggers: ['Polished framing', 'Risk awareness'],          avoid: ['Jargon', 'Casual tone', 'Unvetted commitments'], relationship_signal: 'Halliburton PLEP board session upcoming',     source: 'seeded' },
];

const WORKSTREAM_EDGES = [
  { from: 'Jason Wong',   to: 'Sibanjan Das', label: 'Customer Engine', color: '#6366f1' },
  { from: 'Sibanjan Das', to: 'Jerry Jiang',  label: 'EAI Sprint',     color: '#22C55E' },
  { from: 'Sibanjan Das', to: 'Senthil V.',   label: 'EAI Sprint',     color: '#22C55E' },
  { from: 'Jerry Jiang',  to: 'Senthil V.',   label: 'Sprint planning', color: '#F59E0B' },
];

// ── Zoom helpers ──────────────────────────────────────────────────────────────

function extractZoomParticipantMap(meetings, selfName) {
  const selfFirst = (selfName || '').split(' ')[0].toLowerCase();
  const map = {};
  meetings.forEach(m => {
    (m.participants || []).forEach(pName => {
      if (!pName || pName.length < 2) return;
      // Skip self
      if (selfFirst && pName.toLowerCase().includes(selfFirst)) return;
      const key = pName.trim();
      if (!map[key]) map[key] = { meetings: [], topics: [], actions: [] };
      map[key].meetings.push({
        title: m.meeting_title || m.title || 'Meeting',
        date: m.meeting_date || '',
        sentiment: m.meeting_sentiment || 'neutral',
      });
      (m.key_topics || []).forEach(t => {
        if (!map[key].topics.includes(t)) map[key].topics.push(t);
      });
      // Match action items owned by this participant (fuzzy first name)
      const pFirst = pName.split(' ')[0].toLowerCase();
      (m.action_items || []).forEach(item => {
        if ((item.owner || '').toLowerCase().includes(pFirst)) {
          map[key].actions.push({ ...item, meeting: m.meeting_title });
        }
      });
    });
  });
  return map;
}

function mergeZoomIntoProfiles(base, zoomMap) {
  const enriched = base.map(p => {
    // Match by exact name or first name
    const pFirst = p.name.split(' ')[0].toLowerCase();
    const zoomData = zoomMap[p.name] ||
      Object.entries(zoomMap).find(([k]) =>
        k.toLowerCase().startsWith(pFirst) || pFirst.startsWith(k.split(' ')[0].toLowerCase())
      )?.[1];
    if (zoomData && zoomData.meetings.length > 0) {
      return { ...p, zoom_history: zoomData, has_zoom: true };
    }
    return p;
  });

  // Add zoom-only participants not already in profiles
  const existingFirstNames = enriched.map(p => p.name.split(' ')[0].toLowerCase());
  Object.entries(zoomMap).forEach(([name, zoomData]) => {
    const nameFirst = name.split(' ')[0].toLowerCase();
    if (!existingFirstNames.some(n => n === nameFirst)) {
      enriched.push({
        name,
        role: 'Meeting participant',
        communication_style: 'unknown',
        relationship_health: 'neutral',
        key_topics: [...new Set(zoomData.topics)].slice(0, 5),
        response_triggers: [],
        avoid: [],
        draft_instruction: `${name} has been in ${zoomData.meetings.length} meeting${zoomData.meetings.length !== 1 ? 's' : ''}. Run Analyze to build a full profile.`,
        preferred_tone: 'Run corpus analysis to unlock insights',
        relationship_signal: `Seen in ${zoomData.meetings.length} Zoom meeting${zoomData.meetings.length !== 1 ? 's' : ''}`,
        source: 'zoom',
        has_zoom: true,
        zoom_history: zoomData,
      });
    }
  });

  return enriched;
}

// ── Knowledge Graph ───────────────────────────────────────────────────────────

function KnowledgeGraph({ profiles, userName }) {
  const [hovered, setHovered]           = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);

  const nodes = profiles;
  const cx = 300, cy = 210, r = 155;

  const nodePositions = nodes.map((n, i) => {
    const angle = (2 * Math.PI * i) / nodes.length - Math.PI / 2;
    return { ...n, x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) };
  });

  const healthColor = (h) => HEALTH_COLORS[h] || '#5A607A';
  const styleColor  = (s) => STYLE_COLORS[s]  || '#5A607A';
  const selected    = selectedNode !== null ? nodePositions[selectedNode] : null;

  // Dynamic edges — add zoom-based edges between participants of the same meeting
  const dynamicEdges = [];
  const zoomNodes = nodes.filter(n => n.has_zoom);
  zoomNodes.forEach((a, ai) => {
    zoomNodes.slice(ai + 1).forEach(b => {
      const sharedMeetings = (a.zoom_history?.meetings || []).filter(m =>
        (b.zoom_history?.meetings || []).some(bm => bm.title === m.title)
      );
      if (sharedMeetings.length > 0) {
        const aPos = nodePositions.find(n => n.name === a.name);
        const bPos = nodePositions.find(n => n.name === b.name);
        if (aPos && bPos) dynamicEdges.push({ from: aPos, to: bPos, label: `${sharedMeetings.length} meetings`, color: '#06B6D4' });
      }
    });
  });

  return (
    <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
      <div style={{ flex: '0 0 auto' }}>
        <svg viewBox="0 0 600 430" width="600" height="430"
          style={{ background: '#0a0d16', borderRadius: 12, border: '1px solid #1e2235' }}>
          <defs>
            <radialGradient id="centerGlow">
              <stop offset="0%" stopColor="#6366f1" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#6366f1" stopOpacity="0" />
            </radialGradient>
            <filter id="glow">
              <feGaussianBlur stdDeviation="2.5" result="blur" />
              <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          </defs>

          <circle cx={cx} cy={cy} r={65} fill="url(#centerGlow)" />

          {/* Workstream edges */}
          {WORKSTREAM_EDGES.map((e, i) => {
            const a = nodePositions.find(n => n.name === e.from);
            const b = nodePositions.find(n => n.name === e.to);
            if (!a || !b) return null;
            return <line key={`ws-${i}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y}
              stroke={e.color} strokeWidth={1} strokeOpacity={0.22} strokeDasharray="4 3" />;
          })}

          {/* Zoom co-meeting edges */}
          {dynamicEdges.map((e, i) => (
            <line key={`zm-${i}`} x1={e.from.x} y1={e.from.y} x2={e.to.x} y2={e.to.y}
              stroke={e.color} strokeWidth={1} strokeOpacity={0.2} strokeDasharray="2 4" />
          ))}

          {/* Spokes */}
          {nodePositions.map((n, i) => (
            <line key={`spoke-${i}`} x1={cx} y1={cy} x2={n.x} y2={n.y}
              stroke={healthColor(n.relationship_health)} strokeWidth={selectedNode === i ? 2 : 1}
              strokeOpacity={selectedNode === i ? 0.6 : 0.15} />
          ))}

          {/* Nodes */}
          {nodePositions.map((n, i) => {
            const isHov = hovered === i, isSel = selectedNode === i;
            const nr    = isHov || isSel ? 27 : 23;
            const hc    = healthColor(n.relationship_health);
            const sc    = styleColor(n.communication_style);
            const dx    = n.x - cx, dy = n.y - cy;
            const mag   = Math.sqrt(dx * dx + dy * dy);
            const lx    = n.x + (dx / mag) * (nr + 12);
            const ly    = n.y + (dy / mag) * (nr + 12);
            const ta    = dx > 10 ? 'start' : dx < -10 ? 'end' : 'middle';

            return (
              <g key={i}
                onClick={() => setSelectedNode(selectedNode === i ? null : i)}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                style={{ cursor: 'pointer' }}>
                <circle cx={n.x} cy={n.y} r={nr + 4} fill="none"
                  stroke={hc} strokeWidth={isSel ? 2.5 : 1.5}
                  strokeOpacity={isSel ? 0.9 : 0.3}
                  filter={isSel ? 'url(#glow)' : undefined} />
                <circle cx={n.x} cy={n.y} r={nr}
                  fill={isSel ? '#1e2235' : '#10131e'}
                  stroke={sc} strokeWidth={isSel ? 2 : 1.5} />
                <text x={n.x} y={n.y + 5} textAnchor="middle"
                  fill={hc} fontSize={isSel ? 11 : 10} fontFamily="DM Mono" fontWeight="600">
                  {n.name.split(' ').map(w => w[0]).join('').slice(0, 2)}
                </text>
                {/* Zoom badge */}
                {n.has_zoom && (
                  <g>
                    <circle cx={n.x + nr - 3} cy={n.y - nr + 3} r={6}
                      fill="#06B6D4" fillOpacity={0.9} />
                    <text x={n.x + nr - 3} y={n.y - nr + 7} textAnchor="middle"
                      fill="white" fontSize={7} fontFamily="DM Mono">Z</text>
                  </g>
                )}
                <text x={lx} y={ly} textAnchor={ta}
                  fill={isSel || isHov ? '#e8eaed' : '#8b8fa8'} fontSize={10} fontFamily="DM Mono">
                  {n.name.split(' ')[0]}
                </text>
                <text x={lx} y={ly + 13} textAnchor={ta}
                  fill={isSel || isHov ? hc : '#5a607a'} fontSize={9} fontFamily="DM Mono">
                  {(n.role || '').split(',')[0]}
                </text>
              </g>
            );
          })}

          {/* Center node */}
          <circle cx={cx} cy={cy} r={36} fill="#0a0d16" stroke="#6366f1" strokeWidth={2} />
          <circle cx={cx} cy={cy} r={28} fill="#1a1d2e" stroke="#6366f150" strokeWidth={1} />
          <text x={cx} y={cy - 4} textAnchor="middle"
            fill="#6366f1" fontSize={12} fontFamily="Syne, sans-serif" fontWeight="700">
            {(userName || 'You').split(' ')[0]}
          </text>
          <text x={cx} y={cy + 10} textAnchor="middle" fill="#3a3f55" fontSize={9} fontFamily="DM Mono">you</text>

          {/* Legend — health */}
          <text x={14} y={358} fill="#3a3f55" fontSize={9} fontFamily="DM Mono">Health:</text>
          {[['strong','#22C55E','Strong'],['neutral','#5A607A','Neutral'],['needs_attention','#EF4444','Needs attention']].map(([,c,l],i) => (
            <g key={l}>
              <circle cx={20} cy={370 + i * 17} r={4} fill={c} fillOpacity={0.85} />
              <text x={30} y={374 + i * 17} fill="#5a607a" fontSize={9} fontFamily="DM Mono">{l}</text>
            </g>
          ))}

          {/* Legend — zoom */}
          <circle cx={450} cy={362} r={6} fill="#06B6D4" fillOpacity={0.9} />
          <text x={450} y={366} textAnchor="middle" fill="white" fontSize={7} fontFamily="DM Mono">Z</text>
          <text x={462} y={366} fill="#5a607a" fontSize={9} fontFamily="DM Mono">= seen in Zoom</text>

          {/* Legend — edges */}
          <line x1={440} y1={382} x2={456} y2={382} stroke="#6366f1" strokeWidth={1.5} strokeDasharray="4 3" />
          <text x={462} y={386} fill="#5a607a" fontSize={9} fontFamily="DM Mono">Shared workstream</text>
          <line x1={440} y1={398} x2={456} y2={398} stroke="#06B6D4" strokeWidth={1.5} strokeDasharray="2 4" />
          <text x={462} y={402} fill="#5a607a" fontSize={9} fontFamily="DM Mono">Co-meeting (Zoom)</text>
        </svg>
      </div>

      {/* Detail panel */}
      {selected && (
        <div style={{ flex: 1, minWidth: 260, maxWidth: 340, background: '#10131e', border: `1px solid ${healthColor(selected.relationship_health)}44`, borderLeft: `3px solid ${healthColor(selected.relationship_health)}`, borderRadius: 12, padding: 18, alignSelf: 'flex-start', maxHeight: 430, overflowY: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
            <div>
              <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 16, fontWeight: 700, color: '#e8eaed' }}>{selected.name}</div>
              <div style={{ fontSize: 11, color: '#5a607a', marginTop: 2 }}>{selected.role}</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
              <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 8, background: healthColor(selected.relationship_health) + '22', color: healthColor(selected.relationship_health), fontFamily: 'DM Mono', fontWeight: 600 }}>
                {selected.relationship_health?.replace('_', ' ')}
              </span>
              {selected.has_zoom && (
                <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 8, background: '#06B6D422', color: '#06B6D4', fontFamily: 'DM Mono' }}>
                  🎥 {selected.zoom_history?.meetings?.length} zoom meeting{selected.zoom_history?.meetings?.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>

          {selected.draft_instruction && (
            <div style={{ background: '#6366f111', border: '1px solid #6366f133', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
              <div style={{ fontSize: 9, color: '#6366f1', fontFamily: 'DM Mono', fontWeight: 600, marginBottom: 5 }}>🤖 DRAFT INSTRUCTION</div>
              <div style={{ fontSize: 12, color: '#c8cce0', lineHeight: 1.5 }}>{selected.draft_instruction}</div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
            <div style={{ background: '#0a0d16', border: '1px solid #1e2235', borderRadius: 8, padding: 10 }}>
              <div style={{ fontSize: 9, color: '#5a607a', fontFamily: 'DM Mono', marginBottom: 4 }}>STYLE</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: STYLE_COLORS[selected.communication_style] || '#5a607a' }}>{selected.communication_style}</div>
            </div>
            <div style={{ background: '#0a0d16', border: '1px solid #1e2235', borderRadius: 8, padding: 10 }}>
              <div style={{ fontSize: 9, color: '#5a607a', fontFamily: 'DM Mono', marginBottom: 4 }}>WORKSTREAM</div>
              <div style={{ fontSize: 11, color: '#e8eaed', lineHeight: 1.4 }}>{selected.workstream || '—'}</div>
            </div>
          </div>

          {(selected.response_triggers || []).length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 9, color: '#22c55e', fontFamily: 'DM Mono', marginBottom: 5 }}>✅ WHAT WORKS</div>
              {selected.response_triggers.map((t, i) => <div key={i} style={{ fontSize: 11, color: '#c8cce0', marginBottom: 3 }}>· {t}</div>)}
            </div>
          )}

          {(selected.avoid || []).length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 9, color: '#ef4444', fontFamily: 'DM Mono', marginBottom: 5 }}>⚠️ AVOID</div>
              {selected.avoid.map((a, i) => <div key={i} style={{ fontSize: 11, color: '#c8cce0', marginBottom: 3 }}>· {a}</div>)}
            </div>
          )}

          {(selected.key_topics || []).length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 9, color: '#5a607a', fontFamily: 'DM Mono', marginBottom: 5 }}>🎯 KEY TOPICS</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {selected.key_topics.map((t, i) => (
                  <span key={i} style={{ background: '#1e2235', color: '#6366f1', fontSize: 10, padding: '2px 7px', borderRadius: 14, fontFamily: 'DM Mono' }}>{t}</span>
                ))}
              </div>
            </div>
          )}

          {/* Zoom history section */}
          {selected.has_zoom && selected.zoom_history && (
            <div style={{ borderTop: '1px solid #1e2235', paddingTop: 12, marginTop: 4 }}>
              <div style={{ fontSize: 9, color: '#06B6D4', fontFamily: 'DM Mono', fontWeight: 600, marginBottom: 8 }}>
                🎥 ZOOM HISTORY
              </div>
              {selected.zoom_history.meetings.map((m, i) => {
                const sentCol = { productive: '#22c55e', tense: '#ef4444', inconclusive: '#f59e0b', neutral: '#5a607a' }[m.sentiment] || '#5a607a';
                return (
                  <div key={i} style={{ fontSize: 11, color: '#8b8fa8', marginBottom: 5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>· {m.title}</span>
                    <span style={{ fontSize: 9, color: sentCol, fontFamily: 'DM Mono', marginLeft: 8 }}>{m.sentiment}</span>
                  </div>
                );
              })}

              {selected.zoom_history.actions.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 9, color: '#f59e0b', fontFamily: 'DM Mono', marginBottom: 6 }}>🎯 THEIR OPEN ACTIONS</div>
                  {selected.zoom_history.actions.slice(0, 4).map((a, i) => (
                    <div key={i} style={{ fontSize: 11, color: '#c8cce0', marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}>
                      <span>· {a.action}</span>
                      {a.priority && (
                        <span style={{ fontSize: 9, marginLeft: 6, color: a.priority === 'high' ? '#ef4444' : a.priority === 'medium' ? '#f59e0b' : '#22c55e', fontFamily: 'DM Mono' }}>{a.priority}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {[...new Set(selected.zoom_history.topics)].length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 9, color: '#5a607a', fontFamily: 'DM Mono', marginBottom: 5 }}>📌 ZOOM TOPICS</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {[...new Set(selected.zoom_history.topics)].slice(0, 6).map((t, i) => (
                      <span key={i} style={{ background: '#06B6D422', color: '#06B6D4', fontSize: 9, padding: '2px 7px', borderRadius: 12, fontFamily: 'DM Mono' }}>{t}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div style={{ fontSize: 9, color: '#3a3f55', fontFamily: 'DM Mono', marginTop: 12 }}>
            {selected.source === 'corpus_analysis' ? '🧠 Corpus analysis'
             : selected.source === 'zoom' ? '🎥 Zoom-sourced'
             : selected.source === 'seeded' ? '⚡ Seeded from your network'
             : '🌱 Starter profile'}
            {selected.relationship_signal ? ` · ${selected.relationship_signal}` : ''}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function PeopleTab({ email, name }) {
  const [profiles, setProfiles]           = useState([]);
  const [zoomMeetings, setZoomMeetings]   = useState([]);
  const [loading, setLoading]             = useState(false);
  const [zoomLoading, setZoomLoading]     = useState(false);
  const [analyzing, setAnalyzing]         = useState(false);
  const [activeProfile, setActiveProfile] = useState(null);
  const [customStakeholder, setCustomStakeholder] = useState('');
  const [viewMode, setViewMode]           = useState('graph');

  useEffect(() => {
    if (email) {
      loadProfiles();
      loadZoomMeetings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const loadProfiles = async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/api/stakeholders/profiles?user_email=${encodeURIComponent(email)}`);
      const data = await res.json();
      setProfiles(data.profiles || []);
    } catch (e) {}
    setLoading(false);
  };

  const loadZoomMeetings = async () => {
    setZoomLoading(true);
    try {
      const param = email ? encodeURIComponent(email) : 'default';
      const res   = await fetch(`${API}/api/zoom/meetings?user_email=${param}`);
      const data  = await res.json();
      setZoomMeetings(data.meetings || []);
    } catch (e) {}
    setZoomLoading(false);
  };

  const analyzeStakeholders = async (stakeholders) => {
    setAnalyzing(true);
    try {
      const zoomMap   = extractZoomParticipantMap(zoomMeetings, name);
      const zoomNames = Object.keys(zoomMap);
      const selfFirst = (name || '').split(' ')[0].toLowerCase();

      const toAnalyze = stakeholders.length > 0 ? stakeholders : [
        ...SEED_STAKEHOLDERS.map(s => s.name),
        ...zoomNames.filter(n => !n.toLowerCase().includes(selfFirst)),
      ].filter((n, i, arr) => arr.indexOf(n) === i); // dedup

      const res  = await fetch(`${API}/api/stakeholders/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_email: email, stakeholders: toAnalyze }),
      });
      const data = await res.json();
      const p    = data.profiles || [];

      // Enrich with seed data for starter profiles
      const enriched = p.map(prof => {
        const seed = SEED_STAKEHOLDERS.find(s => s.name === prof.name);
        return seed && prof.source !== 'corpus_analysis' ? { ...seed, ...prof } : prof;
      });
      setProfiles(enriched);
      if (enriched.length > 0) setActiveProfile(enriched[0]);
    } catch (e) {}
    setAnalyzing(false);
  };

  const addCustom = () => {
    if (!customStakeholder.trim()) return;
    analyzeStakeholders([customStakeholder.trim()]);
    setCustomStakeholder('');
  };

  // Build merged display profiles: API profiles (or seeds) + zoom enrichment
  const zoomMap        = extractZoomParticipantMap(zoomMeetings, name);
  const baseProfiles   = profiles.length > 0 ? profiles : SEED_STAKEHOLDERS;
  const displayProfiles = mergeZoomIntoProfiles(baseProfiles, zoomMap);

  const healthColor = (h) => HEALTH_COLORS[h] || '#5A607A';
  const styleColor  = (s) => STYLE_COLORS[s]  || '#5A607A';
  const displayActive = activeProfile || displayProfiles[0];

  const zoomCount = displayProfiles.filter(p => p.has_zoom).length;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
        <span style={{ fontSize: 18 }}>👥</span>
        <span style={{ fontFamily: 'Syne, sans-serif', fontSize: 15, fontWeight: 700, color: '#e8eaed' }}>Stakeholder Intelligence</span>
        <span style={{ background: '#1e2235', color: '#6366f1', fontSize: 10, padding: '2px 8px', borderRadius: 10, fontFamily: 'DM Mono' }}>
          {displayProfiles.length} profiles
        </span>
        {zoomCount > 0 && (
          <span style={{ background: '#06B6D422', color: '#06B6D4', fontSize: 10, padding: '2px 8px', borderRadius: 10, fontFamily: 'DM Mono' }}>
            🎥 {zoomCount} from Zoom
          </span>
        )}
        {zoomLoading && <span style={{ fontSize: 10, color: '#5a607a', fontFamily: 'DM Mono' }}><Spinner />loading zoom...</span>}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <button onClick={() => setViewMode('graph')} style={{ background: viewMode === 'graph' ? '#6366f1' : '#1e2235', border: 'none', color: viewMode === 'graph' ? 'white' : '#8b8fa8', padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 11 }}>🕸 Graph</button>
          <button onClick={() => setViewMode('list')}  style={{ background: viewMode === 'list'  ? '#6366f1' : '#1e2235', border: 'none', color: viewMode === 'list'  ? 'white' : '#8b8fa8', padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 11 }}>☰ List</button>
          <button onClick={() => { loadProfiles(); loadZoomMeetings(); }} style={{ background: '#1e2235', border: 'none', color: '#8b8fa8', padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 11 }}>🔄 Refresh</button>
        </div>
      </div>

      {/* Analysis controls */}
      <div style={{ background: '#10131e', border: '1px solid #1e2235', borderLeft: '3px solid #6366f1', borderRadius: 8, padding: '14px 16px', marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#e8eaed', marginBottom: 6 }}>🧠 Build Intelligence from Your Corpus</div>
        <div style={{ fontSize: 12, color: '#8b8fa8', marginBottom: 12 }}>
          Analyzes emails, meetings, and Zoom transcripts to build communication profiles.
          {zoomMeetings.length > 0 && <span style={{ color: '#06B6D4' }}> · {zoomMeetings.length} Zoom meetings loaded</span>}
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <input
            style={{ flex: 1, background: '#0a0d16', border: '1px solid #1e2235', borderRadius: 6, padding: '7px 10px', color: '#e8eaed', fontSize: 12, outline: 'none', fontFamily: 'inherit' }}
            placeholder="Add a specific name (e.g. Sarah Johnson)..."
            value={customStakeholder}
            onChange={e => setCustomStakeholder(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addCustom()}
          />
          <button onClick={addCustom} disabled={analyzing || !customStakeholder.trim()}
            style={{ padding: '7px 16px', background: '#6366f1', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
            Analyze
          </button>
        </div>
        <button onClick={() => analyzeStakeholders([])} disabled={analyzing}
          style={{ padding: '8px 20px', background: analyzing ? '#2d3148' : '#6366f1', color: 'white', border: 'none', borderRadius: 6, cursor: analyzing ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 500 }}>
          {analyzing ? <><Spinner />Analyzing corpus...</> : '✨ Auto-Detect & Analyze All'}
        </button>
        <div style={{ fontSize: 10, color: '#3a3f55', marginTop: 8, fontFamily: 'DM Mono' }}>
          Picks up names from emails, Zoom transcripts {zoomMeetings.length > 0 ? `(${zoomMeetings.length} loaded)` : ''}, and your seeded network
        </div>
      </div>

      {(loading || analyzing) && (
        <div style={{ color: '#5a607a', fontSize: 13, fontFamily: 'DM Mono', marginBottom: 16 }}>
          <Spinner />{analyzing ? 'Analyzing stakeholders from corpus + Zoom...' : 'Loading profiles...'}
        </div>
      )}

      {/* ── Graph view ── */}
      {viewMode === 'graph' && !loading && !analyzing && (
        <KnowledgeGraph profiles={displayProfiles} userName={name || 'You'} />
      )}

      {/* ── List view ── */}
      {viewMode === 'list' && !loading && !analyzing && (
        <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 20 }}>
          <div>
            {displayProfiles.map((p, i) => (
              <div key={i} onClick={() => setActiveProfile(p)}
                style={{ background: displayActive?.name === p.name ? '#1e2235' : '#10131e', border: `1px solid ${displayActive?.name === p.name ? '#6366f1' : '#1e2235'}`, borderLeft: `3px solid ${healthColor(p.relationship_health)}`, borderRadius: 8, padding: 12, marginBottom: 8, cursor: 'pointer' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#e8eaed' }}>{p.name}</span>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {p.has_zoom && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 6, background: '#06B6D422', color: '#06B6D4', fontFamily: 'DM Mono' }}>🎥</span>}
                    <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 8, background: healthColor(p.relationship_health) + '22', color: healthColor(p.relationship_health), fontFamily: 'DM Mono' }}>
                      {p.relationship_health?.replace('_', ' ')}
                    </span>
                  </div>
                </div>
                <div style={{ fontSize: 11, color: '#5a607a', marginBottom: 4 }}>{p.role || 'Unknown role'}</div>
                <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, background: styleColor(p.communication_style) + '22', color: styleColor(p.communication_style), fontFamily: 'DM Mono' }}>
                  {p.communication_style}
                </span>
              </div>
            ))}
          </div>

          {displayActive && (
            <div style={{ background: '#10131e', border: '1px solid #1e2235', borderLeft: '3px solid #6366f1', borderRadius: 12, padding: 20, maxHeight: 600, overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <div>
                  <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 20, fontWeight: 700, color: '#e8eaed' }}>{displayActive.name}</div>
                  <div style={{ fontSize: 12, color: '#5a607a', marginTop: 2 }}>{displayActive.role}</div>
                </div>
                <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ fontSize: 10, color: healthColor(displayActive.relationship_health), fontFamily: 'DM Mono', fontWeight: 600 }}>
                    {displayActive.relationship_health?.replace('_', ' ').toUpperCase()}
                  </div>
                  {displayActive.has_zoom && (
                    <div style={{ fontSize: 10, color: '#06B6D4', fontFamily: 'DM Mono' }}>
                      🎥 {displayActive.zoom_history?.meetings?.length} Zoom meetings
                    </div>
                  )}
                  <div style={{ fontSize: 10, color: '#3a3f55' }}>{displayActive.relationship_signal}</div>
                </div>
              </div>

              <div style={{ background: '#6366f111', border: '1px solid #6366f133', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
                <div style={{ fontSize: 10, color: '#6366f1', fontFamily: 'DM Mono', fontWeight: 600, marginBottom: 6 }}>🤖 AI DRAFT INSTRUCTION</div>
                <div style={{ fontSize: 13, color: '#c8cce0', lineHeight: 1.5 }}>{displayActive.draft_instruction}</div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div style={{ background: '#0a0d16', border: '1px solid #1e2235', borderRadius: 8, padding: 12 }}>
                  <div style={{ fontSize: 10, color: '#5a607a', fontFamily: 'DM Mono', marginBottom: 6 }}>COMMUNICATION STYLE</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: styleColor(displayActive.communication_style) }}>{displayActive.communication_style}</div>
                </div>
                <div style={{ background: '#0a0d16', border: '1px solid #1e2235', borderRadius: 8, padding: 12 }}>
                  <div style={{ fontSize: 10, color: '#5a607a', fontFamily: 'DM Mono', marginBottom: 6 }}>PREFERRED TONE</div>
                  <div style={{ fontSize: 12, color: '#e8eaed', lineHeight: 1.4 }}>{displayActive.preferred_tone}</div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div style={{ background: '#0a0d16', border: '1px solid #22c55e33', borderRadius: 8, padding: 12 }}>
                  <div style={{ fontSize: 10, color: '#22c55e', fontFamily: 'DM Mono', marginBottom: 8 }}>✅ WHAT WORKS</div>
                  {(displayActive.response_triggers || []).length === 0
                    ? <div style={{ fontSize: 11, color: '#3a3f55' }}>Building from corpus...</div>
                    : (displayActive.response_triggers || []).map((t, i) => <div key={i} style={{ fontSize: 12, color: '#c8cce0', marginBottom: 4 }}>· {t}</div>)}
                </div>
                <div style={{ background: '#0a0d16', border: '1px solid #ef444433', borderRadius: 8, padding: 12 }}>
                  <div style={{ fontSize: 10, color: '#ef4444', fontFamily: 'DM Mono', marginBottom: 8 }}>⚠️ AVOID</div>
                  {(displayActive.avoid || []).length === 0
                    ? <div style={{ fontSize: 11, color: '#3a3f55' }}>Building from corpus...</div>
                    : (displayActive.avoid || []).map((a, i) => <div key={i} style={{ fontSize: 12, color: '#c8cce0', marginBottom: 4 }}>· {a}</div>)}
                </div>
              </div>

              {(displayActive.key_topics || []).length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 10, color: '#5a607a', fontFamily: 'DM Mono', marginBottom: 8 }}>🎯 KEY TOPICS</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {(displayActive.key_topics || []).map((t, i) => (
                      <span key={i} style={{ background: '#1e2235', color: '#6366f1', fontSize: 11, padding: '3px 10px', borderRadius: 20, fontFamily: 'DM Mono' }}>{t}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Zoom history in list view */}
              {displayActive.has_zoom && displayActive.zoom_history && (
                <div style={{ borderTop: '1px solid #1e2235', paddingTop: 14, marginTop: 4 }}>
                  <div style={{ fontSize: 10, color: '#06B6D4', fontFamily: 'DM Mono', fontWeight: 600, marginBottom: 10 }}>
                    🎥 ZOOM HISTORY ({displayActive.zoom_history.meetings.length} meetings)
                  </div>
                  {displayActive.zoom_history.meetings.map((m, i) => {
                    const sentCol = { productive: '#22c55e', tense: '#ef4444', inconclusive: '#f59e0b', neutral: '#5a607a' }[m.sentiment] || '#5a607a';
                    return (
                      <div key={i} style={{ fontSize: 12, color: '#8b8fa8', marginBottom: 5, display: 'flex', justifyContent: 'space-between' }}>
                        <span>· {m.title}</span>
                        <span style={{ fontSize: 10, color: sentCol, fontFamily: 'DM Mono' }}>{m.date}</span>
                      </div>
                    );
                  })}
                  {displayActive.zoom_history.actions.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 10, color: '#f59e0b', fontFamily: 'DM Mono', marginBottom: 8 }}>🎯 THEIR OPEN ACTIONS</div>
                      {displayActive.zoom_history.actions.slice(0, 5).map((a, i) => (
                        <div key={i} style={{ fontSize: 12, color: '#c8cce0', marginBottom: 5, display: 'flex', justifyContent: 'space-between' }}>
                          <span>· {a.action}</span>
                          {a.priority && <span style={{ fontSize: 9, color: a.priority === 'high' ? '#ef4444' : a.priority === 'medium' ? '#f59e0b' : '#22c55e', fontFamily: 'DM Mono' }}>{a.priority}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  {[...new Set(displayActive.zoom_history.topics)].length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 10, color: '#5a607a', fontFamily: 'DM Mono', marginBottom: 6 }}>📌 ZOOM TOPICS</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                        {[...new Set(displayActive.zoom_history.topics)].slice(0, 8).map((t, i) => (
                          <span key={i} style={{ background: '#06B6D422', color: '#06B6D4', fontSize: 10, padding: '2px 8px', borderRadius: 14, fontFamily: 'DM Mono' }}>{t}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div style={{ fontSize: 10, color: '#3a3f55', fontFamily: 'DM Mono', marginTop: 14 }}>
                {displayActive.source === 'corpus_analysis' ? '🧠 Corpus analysis'
                 : displayActive.source === 'zoom' ? '🎥 Zoom-sourced'
                 : displayActive.source === 'seeded' ? '⚡ Seeded from your network'
                 : '🌱 Starter profile'}
                {displayActive.last_updated && ` · Updated ${new Date(displayActive.last_updated).toLocaleDateString()}`}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
